import { useEffect, useMemo, useState } from 'react'
import { topRightToast } from '../Shadcn/Sonner';
import { GeoId } from '../../types/geo.types';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { Close, Search, ChevronUp, ChevronDown, TrashCan, InformationFilled, WarningAltFilled } from '@carbon/icons-react';

// CSS
import '../../styles/main.css'

// Components
import { Typography } from '../Shadcn/Typography';
import { Button } from '../Shadcn/Button';
import { LinkButton } from '../Shadcn/LinkButton';
import { Input } from '../Shadcn/Input';
import { Slider } from '../Shadcn/Slider';
import { Checkbox } from '../Shadcn/Checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogCloseButton } from '../Shadcn/Dialog';
import { TooltipProvider, TooltipRoot, TooltipTrigger, TooltipContent } from '../Shadcn/Tooltip';
import { cn } from '../../lib/utils';
import { useCIFService, useMapsService } from '../../services';
import { brandList, checkboxState, cifTypeState, choroplethModeState, filterDefaultState, filterLoadingState, filtersState, geoState, overlayCountState, poiMapColorState, recommendedRegionsState, selectedBrands, selectedCategoricalLayerState, selectedTabState, selAggLayerState, selPOICategories, userCurrencyState, dynamicTogglesState } from '../../states';
import { addRangeMinMax, cleanFilterTree, extractAllFiltersWithSafeRange, getCountOfChangedRanges, getParentGeoId, hasAdjusted, indexToLetter, isAnyRangeChangedInFilters, removeAllRangeMinMax, resetRangesToMinMax, syncRangesFromFilter, updateRangeByKey, upsertFilterTree, useHandleError } from '../../helpers';
import { BOUNDARY_TYPES, RouteConstants } from '../../constants';


const FILTER_DETAILS: Record<string, { title: string; description: string; chartLabel: string; chartDescription: string }> = {
    my_business_presence: {
        title: 'Business Presence',
        description: 'Counts of your business presences, ie branches, relationship managers, and partners.',
        chartLabel: 'Count of Presences',
        chartDescription: 'The total number of branches, partners, and relationship managers.',
    },
    mbp: {
        title: 'Business Presence',
        description: 'Counts of your business presences, ie branches, relationship managers, and partners.',
        chartLabel: 'Count of Presences',
        chartDescription: 'The total number of branches, partners, and relationship managers.',
    },
    entrepreneurial_households: {
        title: 'Entrepreneurial Households',
        description: 'Entrepreneurial Households (EH) are the bedrock of the EPIC Opportunity, and represent the quantum of customers with a propensity to spend on high priority goods and assets.',
        chartLabel: 'Count of EHs',
        chartDescription: '',
    },
    eh: {
        title: 'Entrepreneurial Households',
        description: 'Entrepreneurial Households (EH) are the bedrock of the EPIC Opportunity, and represent the quantum of customers with a propensity to spend on high priority goods and assets.',
        chartLabel: 'Count of EHs',
        chartDescription: '',
    },
    core_transaction_value: {
        title: 'Core Transaction Value',
        description: "Core Transaction Value (CTV) is EPIC World's unique metric to better understand and measure the economic vibrancy of Entrepreneurial Households.",
        chartLabel: 'Value of Transaction',
        chartDescription: '',
    },
    ctv: {
        title: 'Core Transaction Value',
        description: "Core Transaction Value (CTV) is EPIC World's unique metric to better understand and measure the economic vibrancy of Entrepreneurial Households.",
        chartLabel: 'Value of Transaction',
        chartDescription: '',
    },
    google_map_pols: {
        title: 'Google Map Pols',
        description: 'Google Map Points of Interest data across regions.',
        chartLabel: 'Count of PoIs',
        chartDescription: '',
    },
    gmp: {
        title: 'Google Map Pols',
        description: 'Google Map Points of Interest data across regions.',
        chartLabel: 'Count of PoIs',
        chartDescription: '',
    },
    phonepe_pulse_transactions: {
        title: 'PhonePe Pulse Transactions',
        description: 'Transaction data sourced from PhonePe Pulse across regions.',
        chartLabel: 'Count of Transactions',
        chartDescription: '',
    },
    phonepe: {
        title: 'PhonePe Pulse Transactions',
        description: 'Transaction data sourced from PhonePe Pulse across regions.',
        chartLabel: 'Count of Transactions',
        chartDescription: '',
    },
    national_hospital_directory: {
        title: 'National Hospital Directory',
        description: 'Hospital and healthcare facility data from the National Hospital Directory.',
        chartLabel: 'Count of Hospitals',
        chartDescription: '',
    },
    nhd: {
        title: 'National Hospital Directory',
        description: 'Hospital and healthcare facility data from the National Hospital Directory.',
        chartLabel: 'Count of Hospitals',
        chartDescription: '',
    },
};

const timestampToDateString = (ts?: number): string => {
    if (!ts || isNaN(ts)) return '1970-01-01';
    const d = new Date(ts);
    if (isNaN(d.getTime())) return '1970-01-01';
    return d.toISOString().split('T')[0];
};

// ── FilterRegionModal ──────────────────────────────────────────────────────────

interface FilterRegionModalProps {
    showFilterRegionModal: boolean;
    setShowFilterRegionModal: (showFilterRegionModal: boolean) => void;
    sliders: Record<string, any[]>;
    setSliders: (sliders: Record<string, any[]>) => void;
       hideTrigger?: boolean;

}

export const FilterRegionModal = ({ showFilterRegionModal, setShowFilterRegionModal, sliders, setSliders, hideTrigger = false }: FilterRegionModalProps) => {
    const [searchParams] = useSearchParams();
    const { pathname } = useLocation();
    const cifServices = useCIFService();
    const mapService = useMapsService();
    const { getErrorMsg } = useHandleError();
    const isChecked = useRecoilValue(checkboxState);
    const selectedGeo = useRecoilValue(geoState);
    const cifType = useRecoilValue(cifTypeState);
    const selCategoryList = useRecoilValue(selPOICategories);
    const selCurrency = useRecoilValue(userCurrencyState);
    const poiColors = useRecoilValue(poiMapColorState);
    const [overlays, setOverlays] = useState<any[]>([]);
    const [filters, setFilters] = useRecoilState<any[]>(filtersState);
    const [tempFilters, setTempFilters] = useState<any[]>([]); // Temporary filters for slider changes
    const [filtersdefaults, setFiltersdefault] = useRecoilState<any[]>(filterDefaultState);
    const [isFilterLoading, setIsFilterLoading] = useRecoilState<boolean>(filterLoadingState);
    const { adjacency } = useRecoilValue(checkboxState);
    const [overlayCount, setOverlayCount] = useRecoilState<any>(overlayCountState);
    const [isLoading, setIsLoading] = useState(false);
    const [isSliderMoving, setIsSliderMoving] = useState(false);
    const [districts, setDistricts] = useState<{ filtered_districts: number, all_districts: number }>({ filtered_districts: 0, all_districts: 0 });
    const [selFilter, setSelFilter] = useState<any[]>([]);
    const [localSliders, setLocalSliders] = useState<Record<string, any[]>>({});
    const [selOverlayKey, setSelOverlayKey] = useState<string | null>(null);
    const [selectedUntaggedKey, setSelectedUntaggedKey] = useState<string | null>(null);
    const [hoveredUntaggedKey, setHoveredUntaggedKey] = useState<string | null>(null);
    const [expandedChildIndices, setExpandedChildIndices] = useState<number[]>([]);
    const [showFullDescription, setShowFullDescription] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dynamicToggles = useRecoilValue(dynamicTogglesState);
    const choroplethMode = useRecoilValue(choroplethModeState);
    const selectedCategoricalLayer = useRecoilValue(selectedCategoricalLayerState);




    const getOverlayGroup = (overlay: any): string =>
        overlay.category || 'OTHER';

    const groupedOverlays = useMemo(() => {
        const filtered = overlays.filter(o =>
            !searchTerm || o.overlay.toLowerCase().includes(searchTerm.toLowerCase())
        );
        return filtered.reduce((acc: Record<string, any[]>, overlay) => {
            const group = getOverlayGroup(overlay);
            if (!acc[group]) acc[group] = [];
            acc[group].push(overlay);
            return acc;
        }, {});
    }, [overlays, searchTerm]);

    const selectedOverlayInfo = useMemo(() => {
        return overlays.find((o: any) => o.key === selOverlayKey) ?? null;
    }, [overlays, selOverlayKey]);
    const selectedTab = useRecoilValue(selectedTabState);
    const [recommendedRegionsPayload, setRecommendedRegionsPayload] = useRecoilState(recommendedRegionsState);
    const setBrands = useSetRecoilState(brandList);
    const setSelBrands = useSetRecoilState(selectedBrands);
    const selAggLayer = useRecoilValue(selAggLayerState);
    const catchment = searchParams.get('catchment');

    const geoLevelLabel = useMemo(() => {
        if (selectedGeo.sub_district) return 'villages';
        if (selectedGeo.district) {
            if (selAggLayer?.key === 'subregion' && selAggLayer?.label) {
                return `${selAggLayer.label as string}s`;
            }
            const match = BOUNDARY_TYPES.district.find(o => o.key === selectedTab);
            return match ? `${match.title}s` : 'sub-districts';
        }
        if (selectedGeo.state || selectedGeo.country) return 'districts';
        return 'regions';
    }, [selAggLayer, selectedGeo.state, selectedGeo.district, selectedGeo.sub_district, selectedTab]);


    const geoCode = useMemo(() => {
        const { country, state, district, sub_district, village } = selectedGeo;
        const geo_id = village || sub_district || district || state || country;
        return geo_id === '1' ? 'EW-IN-IND-22-0' : geo_id;
    }, [selectedGeo]);

    const isDisabled =
        (pathname === RouteConstants.root && (!!selectedGeo.village || isChecked?.catchments || selAggLayer?.key === 'hexgrid')) ||
        (pathname === RouteConstants.catchments && catchment !== null && selAggLayer?.key !== 7);


    const filtersForComparison = tempFilters.length > 0 ? tempFilters : filters;
    const isSliderChanged = isAnyRangeChangedInFilters(filtersForComparison, poiColors?.rootProperties?.filters || []);
    const totalFilterCount = Object.values(overlayCount).reduce((acc: number, item) => acc + (item as number), 0);

    const getSliderOverlay = async () => {
        setIsFilterLoading(true);
        try {
            const response = await cifServices.getOverlayFilters(searchParams?.get('catchment') ? (searchParams?.get('catchment')) : geoCode, (geoCode == selectedGeo.district && !selectedGeo.sub_district && !searchParams?.get('catchment')) ? selectedTab : null);
            if (response) setOverlays(response);
        } catch (error: any) {
            getErrorMsg(error);
        } finally {
            setIsFilterLoading(false);
        }
    }

    const fetchFilteredDistricts = async () => {
        const filtersToUse = tempFilters.length > 0 ? tempFilters : filters;
        if (filtersToUse?.length === 0 || (!catchment && selectedGeo.village) || isDisabled) return;

        const cleanedFilters = filtersToUse.map(cleanFilterTree);

        setIsLoading(true);
        try {
            const response = await cifServices.getFilteredDistricts({ filters: cleanedFilters, geoCode: searchParams?.get('catchment') ? (searchParams?.get('catchment')) : geoCode, selCurrency, overlays, sub_level: (geoCode == selectedGeo.district && !selectedGeo.sub_district) ? selectedTab : null });
            if (response) setDistricts(response);
        }
        catch (error: any) {
            getErrorMsg(error);
        } finally {
            setIsLoading(false);
            setIsSliderMoving(false);
        }
    }

    const getPayload = (geoId: GeoId) => {
        geoId = searchParams?.get('catchment') ?? geoId;
        const selMetrics = JSON.parse(localStorage?.getItem("selMetrics") || '{}');

        const metric_type = (!selMetrics || Object.values(selMetrics).filter((item: any) => !!item).length === 0)
            ? cifType.map(item => ({ key: item, ismetric: false }))
            : Object.values(selMetrics)
                .map((metric: any) => {
                    const { title, ...rest } = metric || {};
                    return { ...rest };
                }) || [];
        const choroplethMetrics = Object.entries(dynamicToggles).map(([id, isOn]) => ({ key: id, ismetric: false, isRank: isOn }));

        const metric_type_final = [...metric_type, ...choroplethMetrics];


        return {
            geoId,
            metric_type:metric_type_final,
            filters,
            currency: selCurrency,
            subcategory: selCategoryList.subcategory,
            hex: selectedGeo?.district ? selAggLayer?.key === 'hexgrid' : false,
            // custom_choropleth_ids:{...dynamicToggles},
            ...(choroplethMode === 'categorical' && selectedCategoricalLayer
                ? { 'categorical_filter': selectedCategoricalLayer }
                : {}),
        }
    }

    const openFilterRegionModal = () => {
        localStorage.setItem('filter_new', 'true');
        setShowFilterRegionModal(true);
    };

    const toggleChildExpand = (index: number) => {
        setExpandedChildIndices(prev => prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]);
    };

    const handleSliderChange = (key: string, newRangeMin: number, newRangeMax: number) => {
        setIsSliderMoving(true);

        function findItemRecursive(items: any[]): any | undefined {
            for (const item of items) {
                if (item.key === key) return item;

                if (Array.isArray(item.child_overlay)) {
                    const found = findItemRecursive(item.child_overlay);
                    if (found) return found;
                }

                if (Array.isArray(item.sub_child_overlay)) {
                    const found = findItemRecursive(item.sub_child_overlay);
                    if (found) return found;
                }
            }
            return undefined;
        }

        function upsertRecursiveTree(items: any[]): any[] {
            return items.map(item => {
                const updatedItem = { ...item };

                if (item.key === key) {
                    updatedItem.rangeMin = newRangeMin;
                    updatedItem.rangeMax = newRangeMax;
                    return updatedItem;
                }

                if (Array.isArray(item.child_overlay)) {
                    updatedItem.child_overlay = upsertRecursiveTree(item.child_overlay);
                }

                if (Array.isArray(item.sub_child_overlay)) {
                    updatedItem.sub_child_overlay = upsertRecursiveTree(item.sub_child_overlay);
                }

                return updatedItem;
            });
        }

        const originalItem = findItemRecursive(tempFilters.length > 0 ? tempFilters : filters);

        const clonedLocalSliders: Record<string, any[]> = structuredClone(localSliders);
        const updatedLocalSliders: Record<string, any[]> = {};
        for (const [overlayKey, items] of Object.entries(clonedLocalSliders)) {
            updatedLocalSliders[overlayKey] = upsertRecursiveTree(items);
        }

        const clonedTempFilters = structuredClone(tempFilters.length > 0 ? tempFilters : filters);
        const updatedTempFilters = upsertRecursiveTree(clonedTempFilters);

        const isUserResetToDefault =
            originalItem &&
            originalItem.min === newRangeMin &&
            originalItem.max === newRangeMax &&
            (originalItem.rangeMin !== undefined || originalItem.rangeMax !== undefined);

        let updatedSelFilter = upsertRecursiveTree(structuredClone(selFilter));

        if (isUserResetToDefault) {
            const originalSelItem = findItemRecursive(selFilter);
            if (originalSelItem) {
                updatedSelFilter = updatedSelFilter.map((item: any) =>
                    item.key === key ? updateRangeByKey(item, key, originalSelItem.min, originalSelItem.max) : item
                );
            }
        }

        setSelFilter(updatedSelFilter);
        setLocalSliders(updatedLocalSliders);
        setTempFilters(updatedTempFilters);
    };

    const handleChecksChange = (key: string, value: string, checked: boolean) => {
        const updateItem = (item: any) => {
            if (item.key !== key) return item;
            const allValues = Array.from(new Set((item.categories ?? []).map((c: any) => c.value)));
            const current: string[] = item.selected_categories ?? allValues;
            const next = checked
                ? Array.from(new Set([...current, value]))
                : current.filter((v: string) => v !== value);
            return { ...item, selected_categories: next };
        };
        const updateList = (items: any[]) => items.map(updateItem);

        setSelFilter(prev => updateList(prev));
        setLocalSliders(prev => {
            const updated: Record<string, any[]> = {};
            for (const [k, items] of Object.entries(prev)) updated[k] = updateList(items);
            return updated;
        });
        setTempFilters(prev => updateList(prev.length > 0 ? prev : filters));
    };

    const handleSelFilterChange = (key: string, isdisable: boolean) => {
        if (isdisable) return;

        setSelOverlayKey(key);
        setSelFilter(localSliders[key] ?? []);
        setSelectedUntaggedKey(null);
        setShowFullDescription(false);
    }

    const handleAddFilter = (itemKey: string) => {
        if (!selOverlayKey) return;

        const updatedSelFilter = selFilter
            .filter((s: any) => s.key !== itemKey)
            .concat(selFilter.filter((s: any) => s.key === itemKey).map((s: any) => ({ ...s, is_tagged: true, user_tagged: true, is_removable: true })));

        const updatedLocalSliders = {
            ...localSliders,
            [selOverlayKey]: updatedSelFilter,
        };

        setSelFilter(updatedSelFilter);
        setLocalSliders(updatedLocalSliders);
        setSelectedUntaggedKey(null);
        cifServices.tagSlider([itemKey], 'add').catch(() => {});
    };

    const handleRemoveFilter = (itemKey: string) => {
        if (!selOverlayKey) return;

        const updatedSelFilter = selFilter.map((s: any) =>
            s.key === itemKey ? { ...s, is_tagged: false, user_tagged: false, is_removable: false, rangeMin: undefined, rangeMax: undefined } : s
        );

        const updatedLocalSliders = {
            ...localSliders,
            [selOverlayKey]: updatedSelFilter,
        };

        setSelFilter(updatedSelFilter);
        setLocalSliders(updatedLocalSliders);
        cifServices.tagSlider([itemKey], 'remove').catch(() => {});
    };

    const resetAllFilters = () => {
        const resetLocalSliders: Record<string, any[]> = {};
        for (const [overlayKey, items] of Object.entries(localSliders)) {
            resetLocalSliders[overlayKey] = items.map((slider: any) => resetRangesToMinMax(structuredClone(slider)));
        }

        const filtersToReset = tempFilters.length > 0 ? tempFilters : filters;
        const resetTempFilters = filtersToReset.map((filter: any) => removeAllRangeMinMax(structuredClone(filter)));

        setLocalSliders(resetLocalSliders);
        setTempFilters(resetTempFilters);

        if (selOverlayKey) {
            setSelFilter(resetLocalSliders[selOverlayKey] ?? []);
        }
    };

    const computeOverlayCounts = (slidersMap: Record<string, any[]>) =>
        overlays.reduce((acc: any, overlay: any) => {
            const count = (slidersMap[overlay.key] ?? []).reduce(
                (sum: number, slider: any) => sum + getCountOfChangedRanges(slider), 0
            );
            acc[overlay.key] = count;
            return acc;
        }, {});

    const resetFilter = () => {
        if (!selFilter.length || !selOverlayKey) return;

        const resetSelFilter = selFilter.map((item: any) => resetRangesToMinMax(structuredClone(item)));

        const updatedLocalSliders = { ...localSliders, [selOverlayKey]: resetSelFilter };

        const filtersToUse = tempFilters.length > 0 ? tempFilters : filters;
        const updatedTempFilters = filtersToUse.map((filter: any) =>
            resetSelFilter.some((s: any) => s.key === filter.key)
                ? removeAllRangeMinMax(structuredClone(filter))
                : filter
        );

        setOverlayCount(computeOverlayCounts(updatedLocalSliders));
        setSelFilter(resetSelFilter);
        setLocalSliders(updatedLocalSliders);
        setTempFilters(updatedTempFilters);
    };

    const handleApplyFilter = async () => {
        setIsLoading(true);

        const filtersToApply = tempFilters.length > 0 ? tempFilters : filters;
        const cleanedFilters = filtersToApply.map(cleanFilterTree);

        setFilters(filtersToApply);

        if (geoCode !== (selectedGeo.village) && !searchParams?.get('catchment')) {
            setRecommendedRegionsPayload((prev: any) => ({
                ...prev,
                ...getPayload(geoCode),
                filters: cleanedFilters,
            }));
        }
        try {
            const abortController = new AbortController();
            await mapService.getMapPoiColor({
                ...getPayload(geoCode),
                filters: cleanedFilters,
            }, abortController.signal);

            localStorage.setItem('filters', JSON.stringify(cleanedFilters));
            setOverlayCount(computeOverlayCounts(localSliders));
            setSliders(localSliders);
            setShowFilterRegionModal(false);
            topRightToast.info('Regions have been filtered out based on your selected criteria.');
        } catch (error: any) {
            if (error?.name !== 'AbortError' && error?.name !== 'CanceledError') {
                console.error('Error applying filter:', error);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleCloseModal = () => {
        setTempFilters([]);
        setShowFilterRegionModal(false);
    }

    const updateAllSliderRangeWrtFilters = (newSliders: Record<string, any[]>, filters: any[]) => {
        const result: Record<string, any[]> = {};
        for (const [overlayKey, items] of Object.entries(newSliders)) {
            result[overlayKey] = items.map((slider: any) => {
                const filter = filters.find((f: any) => f.key === slider.key);
                if (!filter) return addRangeMinMax(slider);
                const synced = syncRangesFromFilter(slider, filter);
                if (filter.selected_categories !== undefined) synced.selected_categories = filter.selected_categories;
                return synced;
            });
        }
        return result;
    }

    useEffect(() => {
        if (!showFilterRegionModal) return;

        const firstEnabled = overlays?.find((item: any) => !item.isdisable);
        const firstKey = firstEnabled?.key ?? null;

        setTempFilters(structuredClone(filters));
        setLocalSliders(structuredClone(sliders));
        setSelOverlayKey(firstKey);
        setSelFilter(firstKey ? (sliders[firstKey] ?? []) : []);
    }, [showFilterRegionModal]);

    useEffect(() => {
        getSliderOverlay();
    }, [geoCode, selectedTab, adjacency, catchment, isChecked?.catchments, selAggLayer?.key]);

    useEffect(() => {
        if (overlays.length > 0 && selCurrency) {
            setIsFilterLoading(true);
            const fetchAllSliders = async () => {
                try {
                    const filterFromLs = JSON.parse(localStorage.getItem('filters')!) || [];
                    const rawResponse = await cifServices.getAllFilterSliders(overlays, searchParams?.get('catchment') ? (searchParams?.get('catchment')) : geoCode, selCurrency, (geoCode == selectedGeo.district && !selectedGeo.sub_district) ? selectedTab : null);

                    // rawResponse is any[] — one entry per enabled overlay (in same order).
                    // Each entry is either an array of slider objects (new API) or a single object (old API).
                    const enabledOverlays = overlays.filter((o: any) => !o.isdisable);
                    const enrichedResponse: Record<string, any[]> = {};
                    (rawResponse as any[]).forEach((sliderResult: any, index: number) => {
                        const overlayInfo = enabledOverlays[index];
                        if (!overlayInfo) return;
                        const rawItems: any[] = Array.isArray(sliderResult) ? sliderResult : [sliderResult];
                        // Deduplicate by key — backend occasionally returns duplicate entries
                        const seen = new Set<string>();
                        const items = rawItems.filter((s: any) => {
                            if (!s?.key || seen.has(s.key)) return false;
                            seen.add(s.key);
                            return true;
                        });
                        enrichedResponse[overlayInfo.key] = items.map((slider: any) => ({
                            ...slider,
                            overlay: slider.overlay ?? overlayInfo.overlay,
                            // sub_heading: slider.sub_heading ?? overlayInfo.description,
                        }));
                    });

                    // Flatten all slider items for filters (flat array sent to API)
                    const flatSliders = Object.values(enrichedResponse).flat();

                    setFiltersdefault(flatSliders);
                    let safeFilters = structuredClone(flatSliders);
                    let safeSliders = structuredClone(enrichedResponse);

                    if (filterFromLs?.length > 0) {
                        const filtersWithSafeRange = extractAllFiltersWithSafeRange(safeFilters, filterFromLs);
                        if (hasAdjusted(filtersWithSafeRange) && !isChecked?.catchments) {
                            topRightToast.info(`Filters have been adjusted to match this region's data range`);
                        }

                        setFilters(filtersWithSafeRange);

                        localStorage.setItem('filters', JSON.stringify(filtersWithSafeRange));
                        setIsFilterLoading(false);
                    } else {
                        setFilters(safeFilters);
                        localStorage.setItem('filters', JSON.stringify(safeFilters));
                        setIsFilterLoading(false);
                    }

                    const slidersWithUpdatedRange = updateAllSliderRangeWrtFilters(safeSliders, filterFromLs);
                    setSliders(slidersWithUpdatedRange);

                } catch (error) {
                    console.error('Failed to fetch all filter sliders:', error);
                }
            };

            fetchAllSliders();
        }
    }, [overlays, selCurrency]);

    useEffect(() => {
        const filtersToUse = tempFilters.length > 0 ? tempFilters : filters;
        if (filtersToUse?.length === 0 || (!catchment && selectedGeo.village) || isDisabled) return;

        const handler = setTimeout(() => {
            fetchFilteredDistricts();
        }, 500);

        return () => clearTimeout(handler);
    }, [tempFilters, filters]);

    useEffect(() => {
        if (Object.keys(sliders).length === 0 || overlays.length === 0) return;

        const allOverlayCounts = overlays.reduce((acc: any, overlay: any) => {
            const overlaySliders = sliders[overlay.key] ?? [];
            const count = overlaySliders.reduce((sum: number, slider: any) => sum + getCountOfChangedRanges(slider), 0);
            acc[overlay.key] = count;
            return acc;
        }, {});
        console.log("ALL COUNT",allOverlayCounts)
        setOverlayCount(allOverlayCounts);
    }, [sliders, overlays, tempFilters]);


    return (
        <div className={cn(
            "relative flex flex-row justify-center items-center",
            (isDisabled || isFilterLoading || selAggLayer == null) && 'group disabled'
        )}>
            {!hideTrigger && (
                <>
                    <Button
                        variant="outline"
                        size="small"
                        type='button'
                        onClick={() => openFilterRegionModal()}
                        className='w-full flex flex-row items-center gap-2 justify-center'
                        disabled={isDisabled || isFilterLoading || selAggLayer == null}
                    >
                        {totalFilterCount > 0 ? "Filters" : "Apply Filters"}
                        {!(totalFilterCount === 0 || (selectedGeo.village && !catchment)) && <span className='flex w-5 h-[1.125rem] py-[0.625rem] flex-col justify-center items-center shrink-0 rounded-full bg-brand-light text-[0.625rem]'>{totalFilterCount}</span>}
                    </Button>
                    <div className="absolute z-[200] rounded left-1/2 -translate-x-1/2 top-[120%] w-[10vw] bg-background text-start hidden group-hover:block p-[var(--semantic-xs)] shadow-[0px_-5px_12.7px_0px_rgba(186,186,186,0.25)] before:content-[''] before:absolute before:-top-2 before:left-1/2 before:-translate-x-1/2 before:w-0 before:h-0 before:[border-left:6px_solid_transparent] before:[border-right:6px_solid_transparent] before:[border-bottom:8px_solid_white]">
                        <Typography variant="b2" className="text-muted-foreground">
                            Filters are not applicable at this level
                        </Typography>
                    </div>
                </>
            )}
            <Dialog open={showFilterRegionModal} onOpenChange={(open) => !open && handleCloseModal()}>
                <DialogContent className="w-[66.67vw] h-[80.07vh] p-0 m-0 min-h-auto flex flex-col">
                    <DialogHeader className="border-b border-border shrink-0">
                        <DialogTitle>
                            Filter {geoLevelLabel.charAt(0).toUpperCase() + geoLevelLabel.slice(1)}
                        </DialogTitle>
                        <DialogCloseButton />
                    </DialogHeader>
                    <div className="flex-1 flex flex-row overflow-hidden">
                        <div className="w-1/4 border-r border-border flex flex-col overflow-hidden p-2">
                            <div className="flex items-center">
                                <Input
                                    placeholder="Find Source"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    size="small"
                                    leftIcon={<Search size={16} />}
                                    className="rounded-none border-0 border-b border-border bg-transparent shadow-none focus:ring-0 focus-visible:ring-0 text-xs font-sans"
                                />
                            </div>
                            {searchTerm && (
                                <div className="flex items-center gap-1 px-2 py-1">
                                    {Object.keys(groupedOverlays).length === 0 ? (
                                        <>
                                            <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-epic-neutral-11 text-white shrink-0">
                                                <Close size={10} />
                                            </span>
                                            <Typography variant="b2" weight="regular" className="text-muted-foreground text-[0.65625rem] leading-[1.4]">
                                                No matching results for "{searchTerm}"
                                            </Typography>
                                        </>
                                    ) : (
                                        <>
                                            <Search size={14} className="text-muted-foreground shrink-0" />
                                            <Typography variant="b3" weight="regular" className="text-muted-foreground text-[0.65625rem] leading-[1.4]">
                                                Showing all matching results for "{searchTerm}"
                                            </Typography>
                                        </>
                                    )}
                                </div>
                            )}
                            <hr className='ms-2 me-2 mt-0 mb-0' />
                            <div className="flex-1 overflow-y-auto py-[var(--semantic-xs)]">
                                {Object.entries(groupedOverlays).map(([group, items], groupIndex) => (
                                    <div key={group}>
                                        {groupIndex > 0 && <hr className="ms-2 me-2 my-3" />}
                                        <Typography variant="b2" weight="regular" className="h-5 text-2xs font-medium text-muted-foreground tracking-[0.08em] uppercase px-[var(--semantic-md)] font-mono">{group}</Typography>
                                        {items.map((data: any) => (
                                            <div
                                                key={data.key}
                                                onClick={() => handleSelFilterChange(data.key, data.isdisable)}
                                                className={cn(
                                                    "flex items-center mx-1 py-[var(--semantic-xs)] px-3 w-[calc(100%-0.5rem)] rounded-[var(--semantic-rounded-md)] transition-colors duration-150",
                                                    data.isdisable ? 'pointer-events-none' : 'cursor-pointer hover:bg-muted',
                                                    selOverlayKey === data.key && 'bg-btn-secondary-bg'
                                                )}
                                            >
                                                <Typography
                                                    variant="b1"
                                                    weight="regular"
                                                    className={cn("text-start text-[0.65625rem] leading-[1.4] flex-1", data.isdisable ? "text-muted-foreground" : "text-foreground")}
                                                >
                                                    {data.overlay}
                                                </Typography>
                                                {!data.isdisable && (() => {
                                                    const count = (localSliders[data.key] ?? []).reduce((sum: number, s: any) => sum + getCountOfChangedRanges(s), 0);
                                                    return count > 0 ? (
                                                        <span className="flex min-w-4 h-4 px-1 flex-col justify-center items-center shrink-0 rounded-full bg-brand-light text-[0.5rem] font-semibold ml-1">
                                                            {count}
                                                        </span>
                                                    ) : null;
                                                })()}
                                                {data.isdisable && (
                                                    <TooltipProvider delayDuration={200}>
                                                        <TooltipRoot>
                                                            <TooltipTrigger asChild>
                                                                <InformationFilled className="ml-[0.407vh] w-4 h-4 shrink-0 pointer-events-auto text-epic-neutral-11" />
                                                            </TooltipTrigger>
                                                            <TooltipContent side="right" className="max-w-56">
                                                                This data layer does not support filters at this level because there is no data coverage at this level of granularity.
                                                            </TooltipContent>
                                                        </TooltipRoot>
                                                    </TooltipProvider>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="w-3/4 flex flex-col justify-start items-start overflow-auto">
                            <div className="bg-btn-secondary-bg p-3 w-full flex flex-col gap-[var(--semantic-xs)]">
                                <div className="flex justify-between items-center w-full">
                                    <Typography variant="h4" weight="medium" fontFamily="sans" size="m" className="text-start">
                                        {selectedOverlayInfo?.overlay}
                                    </Typography>
                                    <Button
                                        variant="ghost"
                                        size="mini"
                                        type='button'
                                        onClick={() => resetFilter()}
                                        className='underline p-0 h-auto text-xs text-foreground shrink-0'
                                    >
                                        Reset
                                    </Button>
                                </div>
                                {selectedOverlayInfo?.description && (
                                    <div className="flex flex-col gap-0.5">
                                        <Typography variant="b2" className={cn("text-muted-foreground text-start text-[0.6875rem] leading-[1.5]", !showFullDescription && "line-clamp-4")}>
                                            {selectedOverlayInfo.description}
                                        </Typography>
                                        {selectedOverlayInfo.description.length > 200 && (
                                            <LinkButton
                                                onClick={() => setShowFullDescription(prev => !prev)}
                                                className="self-start text-[0.625rem] p-0 min-h-0"
                                            >
                                                {showFullDescription ? 'Show less' : 'Show more'}
                                            </LinkButton>
                                        )}
                                    </div>
                                )}
                            </div>
                            {/* Tagged items — render with slider */}
                            {selFilter.filter((s: any) => s.is_tagged !== false).map((sliderItem: any, sliderIndex: number) => (
                                <div key={sliderItem.key} className={cn("w-full flex flex-col", sliderIndex > 0 && "border-t border-border")}>
                                    <div className="p-[1.125rem] flex flex-col gap-0.5 w-full">
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                                                {sliderItem.overlay && (
                                                    <Typography variant="b3" className="text-foreground text-start font-sans text-[var(--font-size-s)] font-medium leading-[1.5]">
                                                        {sliderItem.overlay}
                                                    </Typography>
                                                )}
                                                {sliderItem.sub_heading && (
                                                    <Typography variant="b2" className="text-muted-foreground text-start text-xs font-light">
                                                        {sliderItem.sub_heading}
                                                    </Typography>
                                                )}
                                            </div>
                                            <div className="flex flex-col items-end gap-1 shrink-0">
                                               { !sliderItem.is_removable && <span className="flex items-center justify-center self-stretch gap-1 py-[var(--semantic-2xs)] px-[var(--semantic-xs)] min-h-6 rounded-[var(--semantic-rounded-sm)] bg-brand-light">
                                                    <Typography variant="b3" className="text-brand font-medium tracking-[0.08em] uppercase leading-none font-mono">
                                                        {sliderItem.visualization_type ?? 'CHOROPLETH'}
                                                    </Typography>
                                                </span> }
                                                {sliderItem.is_removable && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveFilter(sliderItem.key)}
                                                        className="flex items-center gap-1 text-destructive transition-colors text-[0.65625rem] font-medium"
                                                    >
                                                        <TrashCan size={12} />
                                                        Remove Filter
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div className="w-full mt-3">
                                            {sliderItem.type === 'checks' ? (
                                                <div className="flex flex-wrap gap-2 w-full">
                                                    {(sliderItem.categories ?? []).map((cat: any) => {
                                                        const allValues = Array.from(new Set((sliderItem.categories ?? []).map((c: any) => c.value)));
                                                        const selected_categories: string[] = sliderItem.selected_categories ?? allValues;
                                                        const isSelected = selected_categories.includes(cat.value);
                                                        return (
                                                            <button
                                                                key={cat.value}
                                                                type="button"
                                                                onClick={() => handleChecksChange(sliderItem.key, cat.value, !isSelected)}
                                                                className={cn(
                                                                    "flex flex-1 items-center justify-between gap-3 px-4 py-3 rounded-lg border border-border bg-transparent text-left transition-colors"
                                                                )}
                                                            >
                                                                <div className="flex flex-col gap-0.5">
                                                                    <Typography variant="b3" className="text-foreground font-medium text-xs">
                                                                        {cat.value}
                                                                    </Typography>
                                                                    {cat.count !== undefined && (
                                                                        <Typography variant="b3" className="text-[var(--general-text-secondary)] font-mono font-medium tracking-[0.01em] uppercase">
                                                                            {cat.count} Records
                                                                        </Typography>
                                                                    )}
                                                                </div>
                                                                <Checkbox
                                                                    checked={isSelected}
                                                                    onCheckedChange={(checked) => handleChecksChange(sliderItem.key, cat.value, !!checked)}
                                                                />
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            ) : sliderItem.type === 'date' ? (
                                                <div className="flex flex-col gap-2 w-full">
                                                    <div className="flex items-start gap-2 w-full">
                                                        <div className="flex flex-col gap-1 flex-1">
                                                            <Typography variant="b3" className="text-muted-foreground text-[0.625rem]">From</Typography>
                                                            <input
                                                                type="date"
                                                                min={timestampToDateString(sliderItem.min)}
                                                                max={timestampToDateString(sliderItem.rangeMax ?? sliderItem.max)}
                                                                value={timestampToDateString(sliderItem.rangeMin ?? sliderItem.min)}
                                                                onChange={(e) => handleSliderChange(sliderItem.key, e.target.value ? new Date(e.target.value).getTime() : sliderItem.min ?? 0, sliderItem.rangeMax ?? sliderItem.max ?? 0)}
                                                                className="w-full border border-border rounded-[var(--semantic-rounded-md)] px-3 py-1.5 text-xs font-sans text-foreground bg-background focus:outline-none focus:ring-1 focus:ring-brand"
                                                            />
                                                        </div>
                                                        <div className="flex flex-col gap-1 flex-1">
                                                            <Typography variant="b3" className="text-muted-foreground text-[0.625rem]">To</Typography>
                                                            <input
                                                                type="date"
                                                                min={timestampToDateString(sliderItem.rangeMin ?? sliderItem.min)}
                                                                max={timestampToDateString(sliderItem.max)}
                                                                value={timestampToDateString(sliderItem.rangeMax ?? sliderItem.max)}
                                                                onChange={(e) => handleSliderChange(sliderItem.key, sliderItem.rangeMin ?? sliderItem.min ?? 0, e.target.value ? new Date(e.target.value).getTime() : sliderItem.max ?? 0)}
                                                                className="w-full border border-border rounded-[var(--semantic-rounded-md)] px-3 py-1.5 text-xs font-sans text-foreground bg-background focus:outline-none focus:ring-1 focus:ring-brand"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="flex justify-end">
                                                        <Checkbox
                                                            label="Same as 'From' date"
                                                            checked={(sliderItem.rangeMax ?? sliderItem.max) === (sliderItem.rangeMin ?? sliderItem.min)}
                                                            onCheckedChange={(checked) => {
                                                                const fromVal = sliderItem.rangeMin ?? sliderItem.min ?? 0;
                                                                const toVal = checked ? fromVal : sliderItem.max ?? 0;
                                                                handleSliderChange(sliderItem.key, fromVal, toVal);
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            ) : (
                                                <Slider
                                                    min={sliderItem.min}
                                                    max={sliderItem.max}
                                                    value={[sliderItem.rangeMin ?? sliderItem.min, sliderItem.rangeMax ?? sliderItem.max]}
                                                    onValueChange={([newMin, newMax]) => handleSliderChange(sliderItem.key, newMin, newMax)}
                                                    showMinMax
                                                    size="sm"
                                                />
                                            )}
                                        </div>
                                    </div>
                                    {selOverlayKey === 'points_of_interest' &&
                                        <div className='w-full flex flex-col justify-start items-start gap-1 border-t border-border'>
                                            <Typography variant="b1" className="px-[var(--semantic-md)] pt-[var(--semantic-xs)] text-start font-medium text-[0.75rem]">
                                                Breakdown of PoIs
                                            </Typography>
                                            <Typography variant="b2" className="text-muted-foreground px-[var(--semantic-md)] text-start text-[0.625rem]">
                                                Filter across regions by the individual count of PoI categories and sub-categories
                                            </Typography>
                                        </div>}
                                    {sliderItem?.child_overlay?.length > 0 &&
                                        sliderItem.child_overlay.map((child: any, childIndex: number) => {
                                            const isExpanded = expandedChildIndices.includes(childIndex);
                                            return (
                                                <div
                                                    key={child.overlay}
                                                    className={cn(
                                                        "w-full flex flex-col justify-start items-start gap-2 px-[var(--semantic-md)] py-[var(--semantic-md)]",
                                                        (selOverlayKey !== 'points_of_interest' || childIndex !== 0) && 'border-t border-border'
                                                    )}
                                                >
                                                    <div className="w-full flex justify-between items-center">
                                                        <div className='flex flex-row items-center'>
                                                            <Typography variant="b3" className="text-foreground text-start px-2 py-[0.125rem] font-sans text-[var(--font-size-s)] font-medium leading-[1.5]">
                                                                {`${childIndex + 1}. ${child.overlay} `}
                                                            </Typography>
                                                            <img src={child?.mbp_icon} />
                                                        </div>
                                                        {child?.sub_child_overlay?.length > 0 && (
                                                            <div className="cursor-pointer" onClick={() => toggleChildExpand(childIndex)}>
                                                                {isExpanded
                                                                    ? <ChevronUp size={18} className="text-foreground" />
                                                                    : <ChevronDown size={18} className="text-foreground" />
                                                                }
                                                            </div>
                                                        )}
                                                    </div>
                                                    {selOverlayKey !== 'points_of_interest' && (
                                                        <Typography variant="b2" className="text-muted-foreground text-start px-[var(--semantic-md)] text-[0.75rem]">
                                                            {child.sub_heading}
                                                        </Typography>
                                                    )}
                                                    <div className="w-full flex flex-row justify-between items-center">
                                                        <Slider
                                                            min={child.min}
                                                            max={child.max}
                                                            value={[child.rangeMin ?? child.min, child.rangeMax ?? child.max]}
                                                            onValueChange={([newMin, newMax]) => handleSliderChange(child.key, newMin, newMax)}
                                                            showMinMax
                                                            size="sm"
                                                        />
                                                    </div>
                                                    {child?.sub_child_overlay?.length > 0 && (
                                                        <div className="p-[var(--semantic-md)] w-full" style={{ display: isExpanded ? 'block' : 'none' }}>
                                                            {child.sub_child_overlay.map((subChild: any, subChildIndex: number) => (
                                                                <div
                                                                    key={subChild.overlay}
                                                                    className='w-full flex flex-col justify-start items-start p-[var(--semantic-md)] gap-2'
                                                                >
                                                                    <Typography variant="b2" className="text-foreground text-start text-[0.75rem]">
                                                                        {`${indexToLetter(subChildIndex)}. ${subChild.overlay}`}
                                                                    </Typography>
                                                                    {selOverlayKey !== 'points_of_interest' && (
                                                                        <Typography variant="b2" className="text-muted-foreground text-start text-[0.75rem]">
                                                                            {subChild.sub_heading}
                                                                        </Typography>
                                                                    )}
                                                                    <div className="w-full flex flex-row justify-between items-center">
                                                                        <Slider
                                                                            min={subChild.min}
                                                                            max={subChild.max}
                                                                            value={[subChild.rangeMin ?? subChild.min, subChild.rangeMax ?? subChild.max]}
                                                                            onValueChange={([newMin, newMax]) => handleSliderChange(subChild.key, newMin, newMax)}
                                                                            showMinMax
                                                                            size="sm"
                                                                        />
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                </div>
                            ))}
                            {/* Untagged items — info cards only, no slider */}
                            {selFilter.some((s: any) => s.is_tagged === false) && (
                                <div className="w-full border-t border-border p-[1.125rem] flex flex-col gap-3">
                                    <Typography variant="b1" weight="medium" className="text-start text-[0.75rem]">
                                        More Filters
                                    </Typography>
                                    <div className="grid grid-cols-2 gap-3">
                                        {selFilter.filter((s: any) => s.is_tagged === false).map((item: any) => {
                                            const isSelected = selectedUntaggedKey === item.key || hoveredUntaggedKey === item.key;
                                            return (
                                                <div
                                                    key={item.key}
                                                    onMouseEnter={() => setHoveredUntaggedKey(item.key)}
                                                    onMouseLeave={() => setHoveredUntaggedKey(null)}
                                                    onClick={() => handleAddFilter(item.key)}
                                                    className={cn(
                                                        "flex flex-col gap-1 p-3 rounded-[var(--semantic-rounded-md)] hover:bg-[var(--ds-alpha-brand-2)] border border-dashed cursor-pointer transition-colors duration-150",
                                                        isSelected ? "bg-btn-secondary-bg" : "border-border"
                                                    )}
                                                >
                                                    <div className="flex items-center justify-between gap-2 w-full min-w-0">
                                                        <Typography variant="b1" weight="medium" className="text-start text-[0.75rem] truncate flex-1 min-w-0">
                                                            {item.overlay}
                                                        </Typography>
                                                        {isSelected && item.metadata?.unit && (
                                                            <span className="px-1.5 py-0.5 rounded text-[0.5625rem] font-medium whitespace-nowrap bg-background border border-border text-foreground font-mono shrink-0">
                                                                {item.metadata.unit}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="flex items-end justify-between gap-2 min-h-[1.25rem]">
                                                        {item.sub_heading && (
                                                            <Typography variant="b2" className="text-muted-foreground text-start text-[0.65625rem] leading-[1.4] line-clamp-2 flex-1">
                                                                {item.sub_heading}
                                                            </Typography>
                                                        )}
                                                        {isSelected && (
                                                            <button
                                                                type="button"
                                                                onClick={(e) => { e.stopPropagation(); handleAddFilter(item.key); }}
                                                                className="flex items-center gap-0.5 shrink-0 text-foreground font-semibold text-[0.65625rem] underline whitespace-nowrap"
                                                            >
                                                                + Add Filter
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex flex-row items-center justify-between h-[10%] w-full border-t border-border px-[var(--semantic-xl)] shrink-0">
                        <div className="flex items-center gap-2">
                            {(isLoading || isSliderMoving) ? (
                                <span className="spinner-border spinner-border-sm" style={{ width: '0.65rem', height: '0.65rem', borderWidth: '0.12em' }} />
                            ) : (
                                <InformationFilled className="w-4 h-4 shrink-0 text-epic-neutral-11" />
                            )}
                            <Typography variant="b3" weight="regular" className="text-muted-foreground m-0">
                                {isLoading || isSliderMoving
                                    ? `counting ${geoLevelLabel}`
                                    : `${districts.filtered_districts} of ${districts.all_districts} ${geoLevelLabel} meet your criteria`}
                            </Typography>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="small"
                                rounded="full"
                                type='button'
                                onClick={resetAllFilters}
                            >
                                Reset All Filters
                            </Button>
                            <Button
                                variant="primary"
                                size="small"
                                rounded="full"
                                type='button'
                                className={(isLoading || isSliderMoving) ? 'pointer-events-none' : ''}
                                disabled={isSliderMoving || isLoading}
                                onClick={handleApplyFilter}
                            >
                                {(isLoading || isSliderMoving) ? (
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                ) : (
                                    'View Regions'
                                )}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    )
}
